import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, DestroyRef, inject, OnInit, output, TemplateRef, viewChild, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlexModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';

import { SearchResultComponent } from '@system/search/result/search-result.component';
import { CipoAiContainerComponent } from '../cipo-ai/cipo-ai.component';

export type SearchOverlayContentType = 'search' | 'cipo-ai';

@Component({
  selector: 'cipo-search-overlay',
  templateUrl: './search-overlay.component.html',
  styleUrl: './search-overlay.component.scss',
  imports: [FlexModule, TranslateModule, SearchResultComponent, CipoAiContainerComponent],
})
export class SearchOverlayContainerComponent implements OnInit {
  private readonly overlay = inject(Overlay);
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private readonly overlayContent = viewChild('overlayContent', { read: TemplateRef<HTMLElement> });

  public toggleChange = output<boolean>();

  currentContentType: SearchOverlayContentType = 'search';

  private overlayRef: OverlayRef;
  private overlayConfig: OverlayConfig = {
    hasBackdrop: true, // Enable backdrop to close overlay when clicked
    backdropClass: 'overlay-backdrop', // Custom class for the backdrop
  };

  ngOnInit(): void {
    this.initializeOverlay();
  }

  public toggleOverlay(type: SearchOverlayContentType): void {
    this.currentContentType = type;
    if (this.overlayRef.hasAttached()) {
      this.closeOverlay();
    } else {
      this.updateOverlayPosition();
      this.openOverlay();
    }
  }

  public onDocumentOpened() {
    this.closeOverlay();
  }

  public updateOverlayPosition(rect?: DOMRect): void {
    if (!rect) return;

    const width = rect?.width < 535 ? window.innerWidth - 16 : rect.width;
    const left = rect?.width < 535 ? 8 : rect?.left;

    const position = this.overlay
      .position()
      .global()
      .top('50px')
      .right(`${rect?.right ?? 0}px`)
      .left(`${left ?? 0}px`);

    this.overlayRef.updatePositionStrategy(position);
    this.overlayRef.updateSize({ width: `${width ?? 0}px` });
  }

  private openOverlay(): void {
    this.overlayRef.attach(new TemplatePortal(this.overlayContent(), this.viewContainerRef));
    this.toggleChange.emit(true);
  }

  private closeOverlay(): void {
    this.overlayRef.detach();
    this.toggleChange.emit(false);
  }

  private initializeOverlay(): void {
    this.overlayRef = this.overlay.create(this.overlayConfig);

    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.closeOverlay();
      });
  }
}
